import { useCallback, useMemo } from 'react';
import { useMutation as useTanstackMutation, useQueryClient } from '@tanstack/react-query';

import type { InvalidateQueriesOptions, MutateOptionsNext, MutationOptionsNext, OnSuccessFn } from './types';

import { toast } from '@/modus/Toast';
import type { TLAIApiError } from '@/types/api';
import { safeInvoke } from '@/utils/safe-invoke';

const DEFAULT_LOADING = 'Loading...';
const DEFAULT_SUCCESS = 'Success!';
const DEFAULT_ERROR = 'Error!';

export function useMutationNext<TData = unknown, TError = TLAIApiError, TVariables = void, TOnMutateResult = unknown>({
  toast: defaultToastOption,
  ...tanstackOptions
}: MutationOptionsNext<TData, TError, TVariables, TOnMutateResult>) {
  const queryClient = useQueryClient();

  // TanStack runs the hook-level onSuccess and the per-call onSuccess for the same mutation, so each
  // must act on its own options only: defaulting the per-call one to the hook-level options made every
  // hook-level invalidation run twice (the second refetch cancelling the first) and hook-level
  // onSuccess fire twice.
  const internalSuccessFn = useCallback(
    (
      onSuccess?: OnSuccessFn<TData, TError, TVariables, TOnMutateResult>,
      invalidateQueries?: InvalidateQueriesOptions<TData, TVariables, TOnMutateResult>,
    ) => {
      const successFn: OnSuccessFn<TData, TError, TVariables, TOnMutateResult> = (
        data,
        variables,
        onMutateResult,
        context,
      ) => {
        const queryKeys =
          typeof invalidateQueries === 'function'
            ? invalidateQueries(data, variables, onMutateResult, context)
            : invalidateQueries;

        if (queryKeys?.length) {
          queryKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
        }

        onSuccess?.(data, variables, onMutateResult, context);
      };

      return successFn;
    },
    [queryClient],
  );

  const {
    mutateAsync,
    mutate: _mutate,
    ...result
  } = useTanstackMutation<TData, TError, TVariables, TOnMutateResult>(
    {
      ...tanstackOptions,
      onSuccess: useMemo(
        () => internalSuccessFn(tanstackOptions.onSuccess, tanstackOptions.invalidateQueries),
        [internalSuccessFn, tanstackOptions.onSuccess, tanstackOptions.invalidateQueries],
      ),
    },
    queryClient,
  );

  const internalMutate = useCallback(
    (variables: TVariables, options?: MutateOptionsNext<TData, TError, TVariables, TOnMutateResult>) => {
      const promise = mutateAsync(variables, {
        ...options,
        onSuccess: internalSuccessFn(options?.onSuccess, options?.invalidateQueries),
      });

      const internalToast = options?.toast ?? defaultToastOption;

      if (!internalToast) return promise;

      if (typeof internalToast === 'boolean') {
        return toast
          .promise(promise, {
            loading: DEFAULT_LOADING,
            success: DEFAULT_SUCCESS,
            error: DEFAULT_ERROR,
          })
          .unwrap();
      }

      return toast
        .promise(promise, {
          loading:
            typeof internalToast.loading === 'function'
              ? internalToast.loading(variables)
              : (internalToast.loading ?? DEFAULT_LOADING),
          success: internalToast.success
            ? (data: TData) =>
                typeof internalToast.success === 'function'
                  ? internalToast.success?.(data, variables)
                  : internalToast.success
            : DEFAULT_SUCCESS,
          error: internalToast.error
            ? (error: TError) =>
                typeof internalToast.error === 'function'
                  ? internalToast.error?.(error, variables)
                  : internalToast.error
            : DEFAULT_ERROR,
        })
        .unwrap();
    },
    [internalSuccessFn, mutateAsync, defaultToastOption],
  );

  const mutate = useCallback(
    async (variables: TVariables, options?: MutateOptionsNext<TData, TError, TVariables, TOnMutateResult>) => {
      return safeInvoke<TData, TError>(async () => await internalMutate(variables, options), {
        onError: (error) => console.error('[MUTATION]', error),
      });
    },
    [internalMutate],
  );

  return [mutate, result] as const;
}
